import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Tabs,
    Tab,
    Paper,
    Alert,
    FormControlLabel,
    Switch,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
} from '@mui/material';
import {
    ElectricBolt,
    AccessTime,
    ShowChart,
    Save as SaveIcon,
    CheckCircle,
    Delete
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { vortxColors, vortxTypography } from '../core/theme.js';
import { useAuth } from '../auth/useAuth.js';
import { API_BASE_URL } from '../services/api.js';
import TierEditor from './TierEditor.jsx';
import TOUBuilder from './TOUBuilder.jsx';

function CustomRateBuilder({
    blendedEnergyRate,
    setBlendedEnergyRate,
    blendedDemandRate,
    setBlendedDemandRate,
    vortxSettings,
    setVortxSettings,
    optimizationEngine,
    projectId,
    customTariffData,
    setCustomTariffData,
    monthlyFixedCharge,
    setMonthlyFixedCharge,
    // Export compensation render prop (optional - Site DNA passes this)
    renderExportCompensation,
    // Legacy props for backward compatibility
    exportConfig,
    onExportConfigChange,
    utilityCode,
    showExportCompensation = false,
}) {
    const [tabValue, setTabValue] = React.useState(0);
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [rateName, setRateName] = useState('');
    const [utilityName, setUtilityName] = useState('');
    const [rateScheduleName, setRateScheduleName] = useState('');
    const [effectiveDate, setEffectiveDate] = useState('');
    const [saving, setSaving] = useState(false);
    const { currentUser } = useAuth();

    // Tariff display state
    const [loadedTariffName, setLoadedTariffName] = useState('');
    // Locked rate mode - when a tariff is loaded, this locks to the detected type
    // null = unlocked (custom mode), 'simple'|'tiered'|'tou' = locked to that mode
    const [lockedRateMode, setLockedRateMode] = useState(null);
    // Track tariff source for display: 'custom' | 'saved' | 'approved'
    const [tariffSource, setTariffSource] = useState('custom');

    // Sync state from props when tariff changes (persistence fix)
    useEffect(() => {
        // Restore state from props if we have data (persistence fix)
        // This handles tab switching where component unmounts/remounts
        // AND when a new tariff is selected from UnifiedTariffSelector
        if (customTariffData) {
            setLoadedTariffName(customTariffData.name || 'Loaded Tariff');
            setTariffSource('saved');

            // Extract rate data from tariff (handles different data structures)
            const rd = customTariffData.rate_data || customTariffData;
            const touData = rd.touData || rd;

            // Restore locked mode AND update vortxSettings with tariff rate data
            if (customTariffData.rate_mode === 'tou' || touData?.seasons?.length > 0) {
                setLockedRateMode('tou');
                setTabValue(2);
                // Update vortxSettings.touData so TOUBuilder displays the new tariff
                setVortxSettings?.(prev => ({ ...prev, rateMode: 'tou', touData }));
            } else if (customTariffData.rate_mode === 'tiered' || rd.energyTiers?.length > 0) {
                setLockedRateMode('tiered');
                setTabValue(1);
                setVortxSettings?.(prev => ({ ...prev, rateMode: 'tiered', enableTieredRates: true, energyTiers: rd.energyTiers }));
            } else if (customTariffData.rate_mode) {
                setLockedRateMode(customTariffData.rate_mode);
                setTabValue(['simple', 'tiered', 'tou'].indexOf(customTariffData.rate_mode));
            }

            // Also set dialog names for display
            setUtilityName(customTariffData.utility_name || '');
            setRateScheduleName(customTariffData.rate_schedule_name || '');
        } else {
            // Explicitly clear local loaded state if props are null/empty
            setLoadedTariffName('');
        }
    }, [customTariffData]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        const modes = ['simple', 'tiered', 'tou'];
        if (setVortxSettings) {
            setVortxSettings(prev => ({ ...prev, rateMode: modes[newValue] }));
        }
    };

    /**
     * Clear the currently loaded tariff and reset forms
     */
    const handleClearTariff = () => {
        // 1. Clear loaded tariff state
        setLoadedTariffName('');

        // 2. Clear custom data
        setCustomTariffData?.(null);

        // 3. Reset local inputs
        setBlendedEnergyRate('');
        setBlendedDemandRate('');
        setMonthlyFixedCharge('');

        // 4. Reset detailed data
        setVortxSettings?.(prev => ({
            ...prev,
            rateMode: 'simple',
            enableTieredRates: false,
            energyTiers: [],
            touData: null
        }));

        // 5. Unlock and reset mode
        setLockedRateMode(null);
        setTariffSource('custom');
        setTabValue(0);

        // 6. Clear dialog state
        setUtilityName('');
        setRateScheduleName('');
    };

    // Sync initial tab if mode exists
    React.useEffect(() => {
        if (vortxSettings?.rateMode) {
            const modes = ['simple', 'tiered', 'tou'];
            const idx = modes.indexOf(vortxSettings.rateMode);
            if (idx !== -1 && idx !== tabValue) setTabValue(idx);
        }
    }, []);

    const handleSaveRate = async () => {
        if (!currentUser || !rateName.trim()) return;
        setSaving(true);

        try {
            const token = await currentUser.getIdToken();
            const modes = ['simple', 'tiered', 'tou'];

            const payload = {
                name: rateName.trim(),
                utility_name: utilityName.trim(),
                rate_schedule_name: rateScheduleName.trim(),
                effective_date: effectiveDate || null,
                rate_mode: modes[tabValue],
                source_project_id: projectId,
                rate_data: {
                    blendedEnergyRate,
                    blendedDemandRate,
                    enableTieredRates: vortxSettings?.enableTieredRates,
                    energyTiers: vortxSettings?.energyTiers,
                    touData: vortxSettings?.touData
                }
            };

            const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/custom-rates`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                setSaveDialogOpen(false);
                setRateName('');
                // Note: UnifiedTariffSelector will show the saved rate after refresh
            }
        } catch (err) {
            console.error('Error saving rate:', err);
        } finally {
            setSaving(false);
        }
    };

    const isVortxFlux = optimizationEngine === 'vortx_flux';

    return (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: alpha(vortxColors.gold, 0.02), borderColor: alpha(vortxColors.gold, 0.2) }}>
            {/* Rate section */}
            <>
                    {/* Currently Loaded Tariff Header */}
                    {loadedTariffName ? (
                        <Box sx={{
                            mb: 2,
                            p: 2,
                            bgcolor: alpha(vortxColors.gold, 0.15),
                            borderRadius: 1,
                            border: `2px solid ${alpha(vortxColors.gold, 0.5)}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1.5
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <CheckCircle sx={{ fontSize: 28, color: vortxColors.gold }} />
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: vortxColors.gold, fontFamily: vortxTypography.heading, lineHeight: 1.2 }}>
                                        {loadedTariffName}
                                    </Typography>
                                    {utilityName && (
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {utilityName} • {rateScheduleName || 'Custom Rate'}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {/* Only show Save Rate for manually entered tariffs (no database ID) */}
                                {!customTariffData?.id && (
                                    <Button
                                        startIcon={<SaveIcon />}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => setSaveDialogOpen(true)}
                                        sx={{ borderColor: vortxColors.gold, color: vortxColors.gold }}
                                    >
                                        Save Rate
                                    </Button>
                                )}
                                <Button
                                    startIcon={<Delete />}
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    onClick={handleClearTariff}
                                >
                                    Clear
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                            <Typography variant="subtitle2" color="text.secondary">
                                No tariff loaded. Use the selector above or enter values manually.
                            </Typography>
                            <Button
                                startIcon={<SaveIcon />}
                                variant="outlined"
                                size="small"
                                onClick={() => setSaveDialogOpen(true)}
                                sx={{ borderColor: vortxColors.gold, color: vortxColors.gold }}
                            >
                                Save Rate
                            </Button>
                        </Box>
                    )}

                    {/* Tabs - locked when tariff determines rate type */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                        <Tabs value={tabValue} onChange={handleTabChange} aria-label="custom rate tabs">
                            <Tab
                                icon={<ElectricBolt />}
                                iconPosition="start"
                                label="Simple (Blended)"
                                disabled={lockedRateMode && lockedRateMode !== 'simple'}
                                sx={lockedRateMode && lockedRateMode !== 'simple' ? { opacity: 0.4 } : {}}
                            />
                            <Tab
                                icon={<ShowChart />}
                                iconPosition="start"
                                label="Tiered (Block)"
                                disabled={!isVortxFlux || (lockedRateMode && lockedRateMode !== 'tiered')}
                                sx={lockedRateMode && lockedRateMode !== 'tiered' ? { opacity: 0.4 } : {}}
                            />
                            <Tab
                                icon={<AccessTime />}
                                iconPosition="start"
                                label="Time-of-Use (TOU)"
                                disabled={lockedRateMode && lockedRateMode !== 'tou'}
                                sx={lockedRateMode && lockedRateMode !== 'tou' ? { opacity: 0.4 } : {}}
                            />
                        </Tabs>
                    </Box>

                    {/* Tab 0: Simple */}
                    {tabValue === 0 && (
                        <Box>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Use simple annual averages for energy and demand charges. Good for quick estimates.
                            </Alert>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    label="Blended Energy Rate ($/kWh)"
                                    type="number"
                                    fullWidth
                                    value={blendedEnergyRate || ''}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        setBlendedEnergyRate(newValue);
                                        // Update customTariffData so monthly performance can find the tariff
                                        if (setCustomTariffData && (newValue || blendedDemandRate)) {
                                            setCustomTariffData({
                                                name: 'Manual Blended Rate',
                                                rate_mode: 'simple',
                                                rate_data: {
                                                    blendedEnergyRate: parseFloat(newValue) || 0,
                                                    blendedDemandRate: parseFloat(blendedDemandRate) || 0,
                                                }
                                            });
                                        }
                                    }}
                                    helperText="Annual average price per kWh"
                                    sx={{ flex: 1 }}
                                />
                                <TextField
                                    label="Blended Demand Rate ($/kW)"
                                    type="number"
                                    fullWidth
                                    value={blendedDemandRate || ''}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        setBlendedDemandRate(newValue);
                                        // Update customTariffData so monthly performance can find the tariff
                                        if (setCustomTariffData && (blendedEnergyRate || newValue)) {
                                            setCustomTariffData({
                                                name: 'Manual Blended Rate',
                                                rate_mode: 'simple',
                                                rate_data: {
                                                    blendedEnergyRate: parseFloat(blendedEnergyRate) || 0,
                                                    blendedDemandRate: parseFloat(newValue) || 0,
                                                }
                                            });
                                        }
                                    }}
                                    helperText="Annual average price per kW"
                                    sx={{ flex: 1 }}
                                />
                            </Box>
                        </Box>
                    )}

                    {/* Tab 1: Tiered */}
                    {tabValue === 1 && (
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle2">Tiered Energy Rates</Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={vortxSettings?.enableTieredRates || false}
                                            onChange={(e) => {
                                                setVortxSettings(prev => ({ ...prev, enableTieredRates: e.target.checked }));
                                            }}
                                            color="primary"
                                        />
                                    }
                                    label="Enable Tiered Rates"
                                />
                            </Box>

                            {vortxSettings?.enableTieredRates ? (
                                <TierEditor
                                    tiers={vortxSettings?.energyTiers || []}
                                    onChange={(tiers) => setVortxSettings(prev => ({ ...prev, energyTiers: tiers }))}
                                />
                            ) : (
                                <Alert severity="warning">
                                    Enable tiered rates to define usage-based pricing blocks.
                                </Alert>
                            )}

                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Demand Charges</Typography>
                                <TextField
                                    label="Flat Demand Rate ($/kW)"
                                    type="number"
                                    fullWidth
                                    value={blendedDemandRate || ''}
                                    onChange={(e) => setBlendedDemandRate(e.target.value)}
                                    helperText="Applied alongside tiered energy rates"
                                />
                            </Box>
                        </Box>
                    )}

                    {/* Tab 2: TOU */}
                    {tabValue === 2 && (
                        <TOUBuilder
                            touData={vortxSettings?.touData}
                            onChange={(data) => setVortxSettings(prev => ({ ...prev, touData: data }))}
                            readOnly={tariffSource !== 'custom'}
                            onUnlock={() => setTariffSource('custom')}
                            tariffSource={tariffSource}
                            renderExportCompensation={renderExportCompensation}
                        />
                    )}

                    {/* Save Rate Dialog */}
                    <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
                        <DialogTitle sx={{ color: vortxColors.gold }}>Save Rate Structure</DialogTitle>
                        <DialogContent>
                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                <TextField
                                    margin="dense"
                                    label="Utility Name"
                                    value={utilityName}
                                    onChange={(e) => setUtilityName(e.target.value)}
                                    placeholder="e.g., SCE, PG&E, SDG&E"
                                    helperText="Utility provider"
                                    sx={{ flex: 1 }}
                                />
                                <TextField
                                    margin="dense"
                                    label="Rate Schedule"
                                    value={rateScheduleName}
                                    onChange={(e) => setRateScheduleName(e.target.value)}
                                    placeholder="e.g., TOU-GS-3, A-10"
                                    helperText="Official rate name"
                                    sx={{ flex: 1 }}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                <TextField
                                    autoFocus
                                    margin="dense"
                                    label="Display Name"
                                    value={rateName}
                                    onChange={(e) => setRateName(e.target.value)}
                                    placeholder="e.g., SCE TOU-GS-3 Summer 2024"
                                    helperText="Custom label to identify this rate"
                                    sx={{ flex: 2 }}
                                />
                                <TextField
                                    margin="dense"
                                    label="Effective Date"
                                    type="date"
                                    value={effectiveDate}
                                    onChange={(e) => setEffectiveDate(e.target.value)}
                                    helperText="When rate takes effect"
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ flex: 1 }}
                                />
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => {
                                setSaveDialogOpen(false);
                                setRateName('');
                                setUtilityName('');
                                setRateScheduleName('');
                                setEffectiveDate('');
                            }}>Cancel</Button>
                            <Button
                                onClick={handleSaveRate}
                                disabled={!rateName.trim() || saving}
                                variant="contained"
                                sx={{ bgcolor: vortxColors.gold }}
                            >
                                {saving ? <CircularProgress size={20} /> : 'Save'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            )}
        </Paper>
    );
}

export default CustomRateBuilder;
